import { CheckIcon, FolderIcon, PlusIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import { ProjectPickerRow } from '@/components/projects/ProjectPickerRow';
import { ResponsiveMenuSub } from '@/components/projects/ResponsiveMenuSub';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/use-auth';
import { MENU_ITEM_MOBILE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { campaignService } from '@/services/data/CampaignService';
import type { PlatformType } from '@/services/socialAuth/types';
import { getPlatformIcon } from '@/utils/platformUtils';

export interface AddToProjectSubmenuProps {
  /** Project id the entity is currently attached to. */
  currentProjectId?: string | null;
  /** Caller wires the actual attachment service call. Receives the chosen project id. */
  onSelectProject: (projectId: string) => void;
  /** Override label — defaults to "Add to campaign". */
  label?: string;
}

/**
 * Canonical "Add to project" submenu reused across the app's dropdown menus.
 * Renders active projects with platform icons + a checkmark on the current
 * project, and a "Create new campaign..." footer.
 *
 * This component is purely UI — the caller supplies the attachment handler
 * via `onSelectProject` so each entity type (session/document/post) routes
 * through the appropriate service.
 */
export function AddToProjectSubmenu({
  currentProjectId,
  onSelectProject,
  label,
}: AddToProjectSubmenuProps): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: campaigns = [] } = useQuery({
    queryKey: ['add-to-plan-menu', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const result = await campaignService.listCampaignsWithCoverage(user.id);
      return (result.data ?? []).filter((c) =>
        ['draft', 'active', 'generating', 'partial'].includes(c.status),
      );
    },
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  const handleCreateNewPlan = (): void => {
    navigate('/?mode=plan');
  };

  const triggerLabel = label ?? 'Add to campaign';

  return (
    <ResponsiveMenuSub
      icon={<FolderIcon className="size-4" weight="regular" />}
      label={triggerLabel}
    >
      {campaigns.length === 0 ? (
        <div className="px-2 py-2 text-detail text-muted-foreground">No active campaigns</div>
      ) : (
        campaigns.map((plan) => {
          const platforms = (plan.platforms ?? []).slice(0, 3);
          const isCurrent = plan.id === currentProjectId;
          return (
            <DropdownMenuItem
              key={plan.id}
              onSelect={(e) => {
                e.preventDefault();
                onSelectProject(plan.id);
              }}
              title={plan.name}
              className={cn(MENU_ITEM_MOBILE, 'rounded-sm', isCurrent && 'opacity-80')}
            >
              <ProjectPickerRow
                name={plan.name}
                icon={plan.icon}
                color={plan.color}
                trailing={
                  <span className="flex items-center gap-1 shrink-0">
                    {plan.createdAt && (
                      <span className="text-detail text-muted-foreground">
                        {format(new Date(plan.createdAt), 'MMM d')}
                      </span>
                    )}
                    {platforms.length > 0 &&
                      platforms.map((p) => {
                        const Icon = getPlatformIcon(p as PlatformType);
                        return (
                          <Icon key={p} className="size-3 text-muted-foreground" weight="regular" />
                        );
                      })}
                    {isCurrent && <CheckIcon className="size-3.5 text-foreground" weight="bold" />}
                  </span>
                }
              />
            </DropdownMenuItem>
          );
        })
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onSelect={handleCreateNewPlan}
        className={cn(MENU_ITEM_MOBILE, 'rounded-sm')}
      >
        <PlusIcon className="size-4" weight="regular" />
        <span>Create new campaign...</span>
      </DropdownMenuItem>
    </ResponsiveMenuSub>
  );
}
